import './applicationUpdatePage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { PHONE_NUMBER_PATTERN } from '@thesavingsgroup/regex';
import { toDigits } from '@thesavingsgroup/utils/toDigits';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import vehicleSUVSideView from '../../assets/vehicle-suv-sideview-one.png';
import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { NumberInput } from '../../components/NumberInput/NumberInput';
import { Rate } from '../../components/Rate/Rate';
import useApiStore from '../../store/apiStore/useApiStore';
import useApplicationUpdateStore from '../../store/applicationUpdateStore/useApplicationUpdateStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../store/pinStore/usePinStore';
import useRouteStore from '../../store/routeStore/useRouteStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { t } = useTranslation();
  const {
    primaryPhoneNumber: pinDeliveredPhoneNumber,
    deliverPin,
    deliverPinFailedMessage,
  } = usePinStore();
  const {
    autopayNumber,
    amount,
    apr,
    term,
    phoneNumber: parameterPhoneNumber,
  } = useParameterStore();
  const { setAuthRedirectPath } = useRouteStore();

  const {
    computed: {
      displayOfferInformation,
      titleText,
      shouldReportViewedOffer,
      subtitleText,
    },
    reportViewedOffer,
  } = useApplicationUpdateStore();

  const form = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      primaryPhoneNumber: toPhoneString(
        pinDeliveredPhoneNumber || parameterPhoneNumber,
      ),
    },
    delayError: 200,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const phoneNumberValidation = {
    setValueAs: (value: string): string => toDigits(value),
    required: t('validation.phone.required'),
    pattern: {
      value: PHONE_NUMBER_PATTERN,
      message: t('validation.phone.invalid'),
    },
  };

  const handleRedirect = (): void => {
    usePinStore.setState({ deliverPinFailedMessage: null });
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { primaryPhoneNumber } = values;

    await deliverPin({
      deliverPinLocation: DeliverPinLocation.SIGN_IN,
      phone: primaryPhoneNumber,
      purpose: AuthType.ACCOUNT_SIGN_IN_USING_PIN,
    });

    const { deliverPinSuccess } = usePinStore.getState();

    if (deliverPinSuccess) {
      navigate('/sign-in/pin-verify');
    }
  };

  useEffect((): void => {
    if (shouldReportViewedOffer) {
      reportViewedOffer();
    }
  }, [shouldReportViewedOffer]);

  useEffect((): void => {
    // Ensure the user is redirected to the application page upon successfully authentication
    if (autopayNumber) {
      setAuthRedirectPath(`/dashboard/application/${autopayNumber}`);
    }
  }, [autopayNumber]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page application-update-page">
        <Header showLogo splitLayout />

        <main>
          <figure>
            <img
              alt={t('imageAlts.vehicleSUVSideView')}
              src={vehicleSUVSideView}
            />
          </figure>
          <form
            data-testid={locator.pages.applicationUpdate.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicationUpdate.form.h1}>
                {titleText}
              </h1>
              <h2 data-testid={locator.pages.applicationUpdate.form.h2}>
                {subtitleText}
              </h2>

              {displayOfferInformation && (
                <Rate
                  apr={apr}
                  monthlyPaymentAmount={amount}
                  term={term}
                  calculateMonthlyPaymentAmount
                />
              )}

              {deliverPinFailedMessage && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.applicationUpdate.alert.error}
                    severity="error"
                  >
                    <Trans
                      components={{
                        signin: (
                          <Link
                            data-testid={
                              locator.pages.applicationUpdate.alert.signInLink
                            }
                            to="/sign-in"
                            onClick={handleRedirect}
                          />
                        ),
                        signup: (
                          <Link
                            data-testid={
                              locator.pages.applicationUpdate.alert.signUpLink
                            }
                            to="/sign-up"
                            onClick={handleRedirect}
                          />
                        ),
                      }}
                    >
                      {deliverPinFailedMessage}
                    </Trans>
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                {/*
                  In the case where the user has already entered a phone number, had a pin delivered,
                  and pressed the back button we should display the phone number that was last entered.

                  Otherwise, we should display the phone number that was passed in as a query parameter.
                */}
                <NumberInput
                  {...register('primaryPhoneNumber', phoneNumberValidation)}
                  autoComplete="tel-national"
                  data-testid={locator.pages.applicationUpdate.form.phoneInput}
                  error={errors.primaryPhoneNumber}
                  errorTestId={
                    locator.pages.applicationUpdate.form.phoneInputError
                  }
                  label={t('ApplicationUpdatePage.phoneNumber')}
                  phone
                />
              </div>

              <Button
                data-testid={locator.pages.applicationUpdate.form.submitButton}
                loader={isLoading}
                loaderPosition="start"
                fullWidth
                submit
              >
                {t('ApplicationUpdatePage.continue')}
              </Button>
            </div>
            <footer data-testid={locator.pages.applicationUpdate.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicationUpdatePage';
